import React, {
    useEffect,
    useMemo,
    useRef,
    useState,
    useCallback,
} from "react";
import axios from "axios";
import {
    Box,
    Grid,
    Paper,
    Typography,
    Button,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    Card,
    CardContent,
    Stack,
    Tooltip as MuiTooltip,
    IconButton,
    Chip,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    List,
    ListItem,
    ListItemText,
    Divider,
    Alert,
    Badge,
    LinearProgress,
    Tabs,
    Tab,
    Link,
} from "@mui/material";
import { styled, useTheme, alpha, darken, lighten } from "@mui/material/styles";
import {
    DataGridPro,
    GridToolbar,
    GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
    GRID_DETAIL_PANEL_TOGGLE_FIELD,
} from "@mui/x-data-grid-pro";

/* Icons */
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import AssessmentIcon from "@mui/icons-material/Assessment";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import VisibilityIcon from "@mui/icons-material/Visibility";
import SchoolIcon from "@mui/icons-material/School";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import BusinessIcon from "@mui/icons-material/Business";
import LaunchIcon from "@mui/icons-material/Launch";
import RefreshIcon from "@mui/icons-material/Refresh";
import AttachMoneyIcon from "@mui/icons-material/AttachMoney";
import CloseIcon from "@mui/icons-material/Close";
import SavingsIcon from "@mui/icons-material/Savings";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import ImageIcon from "@mui/icons-material/Image";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";

import {
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ResponsiveContainer,
    BarChart,
    Bar,
    PieChart,
    Pie,
    Cell,
    ComposedChart,
} from "recharts";
import * as XLSX from 'xlsx';

/* ------------------------------------------------------------------------- */
/* API ENDPOINT CONSTANTS - UPDATED TO USE VIEWS                            */
/* ------------------------------------------------------------------------- */
const API_BASE = "https://figazuresql.swancreate.com/api/swa";

// Use the new views for optimized queries
const API_INVOICE_ITEMS_MASTER = `${API_BASE}/vw_InvoiceItemsMaster`;
const API_INVOICE_STATS_OVERALL = `${API_BASE}/vw_InvoiceStatisticsOverall`;
const API_VENDOR_VARIANCE_ANALYSIS = `${API_BASE}/vw_VendorVarianceAnalysis`;
const API_INVOICE_MONTHLY_TRENDS = `${API_BASE}/vw_InvoiceMonthlyTrends`;

// Legacy endpoints for updates and school data
const API_AZ_INVOICEITEMS = `${API_BASE}/AZ_InvoiceItems`;
const API_SCHOOLMST = `${API_BASE}/SchoolMst`;
const API_AZ_INVOICE = `${API_BASE}/AZ_Invoice`;

/* ------------------------------- Helpers --------------------------------- */
const fmtUSD = (n) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(
        Number(n || 0)
    );

const fmtPercent = (n) =>
    new Intl.NumberFormat("en-US", { style: "percent", minimumFractionDigits: 1 }).format(
        Number(n || 0)
    );

const dateKey = (d) =>
    d
        ? new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()))
            .toISOString()
            .slice(0, 10)
        : "";

const schoolYearOf = (d) => {
    if (!d) return "Unknown";
    const y = d.getFullYear();
    const m = d.getMonth(); // 0=Jan
    return m >= 6 ? `${y}-${y + 1}` : `${y - 1}-${y}`;
};

const schoolYearBounds = (label) => {
    const [startY, endY] = label.split("-").map((s) => parseInt(s, 10));
    return [new Date(startY, 6, 1), new Date(endY, 5, 30, 23, 59, 59, 999)];
};

const enumerateDays = (start, end) => {
    const out = [];
    const cur = new Date(
        Date.UTC(start.getFullYear(), start.getMonth(), start.getDate())
    );
    const last = new Date(
        Date.UTC(end.getFullYear(), end.getMonth(), end.getDate())
    );
    while (cur <= last) {
        out.push(new Date(cur));
        cur.setUTCDate(cur.getUTCDate() + 1);
    }
    return out;
};

/* ============================ THEME'D GRID ============================ */
const StyledDataGridPro = styled(DataGridPro)(({ theme }) => {
    const isDark = theme.palette.mode === "dark";
    const headerBg = isDark
        ? darken(theme.palette.background.default, 0.25)
        : lighten(theme.palette.background.default, 0.06);

    const headerFg = theme.palette.getContrastText(headerBg);

    const zebraEven = isDark
        ? alpha(theme.palette.primary.light, 0.1)
        : alpha(theme.palette.primary.main, 0.04);

    const zebraOdd = isDark
        ? alpha(theme.palette.primary.main, 0.08)
        : alpha(theme.palette.primary.light, 0.06);

    const overTint = isDark
        ? alpha(theme.palette.error.light, 0.18)
        : alpha(theme.palette.error.main, 0.14);

    const underTint = isDark
        ? alpha(theme.palette.success.light, 0.18)
        : alpha(theme.palette.success.main, 0.14);

    return {
        border: "none",
        backgroundColor: theme.palette.background.paper,
        color: theme.palette.text.primary,
        borderRadius: theme.radii?.lg || 12,

        "& .MuiDataGrid-withBorderColor": {
            borderColor: theme.palette.divider,
        },

        "& .MuiDataGrid-columnHeaders": {
            backgroundColor: headerBg,
            color: headerFg,
            borderBottom: `1px solid ${theme.palette.divider}`,
            fontWeight: 700,
        },
        "& .MuiDataGrid-columnHeaderTitle": {
            color: headerFg,
            fontWeight: 700,
        },
        "& .MuiDataGrid-iconSeparator": {
            color: alpha(headerFg, 0.4),
        },
        "& .MuiDataGrid-sortIcon": {
            color: headerFg,
        },

        "& .MuiDataGrid-toolbarContainer": {
            backgroundColor: headerBg,
            color: headerFg,
            paddingInline: 8,
            borderBottom: `1px solid ${theme.palette.divider}`,
            gap: 8,
        },
        "& .MuiDataGrid-toolbarContainer .MuiButton-root, \
       .MuiDataGrid-toolbarContainer .MuiSvgIcon-root, \
       .MuiDataGrid-toolbarContainer .MuiInputBase-root, \
       .MuiDataGrid-toolbarContainer .MuiInputBase-input": {
            color: headerFg,
        },
        "& .MuiDataGrid-toolbarContainer .MuiInputBase-input::placeholder": {
            color: alpha(headerFg, 0.75),
            opacity: 1,
        },

        "& .MuiDataGrid-footerContainer": {
            backgroundColor: headerBg,
            color: headerFg,
            borderTop: `1px solid ${theme.palette.divider}`,
        },

        "& .MuiDataGrid-cell": {
            borderBottom: `1px solid ${theme.palette.divider}`,
        },

        "& .MuiDataGrid-row--even": { backgroundColor: zebraEven },
        "& .MuiDataGrid-row--odd": { backgroundColor: zebraOdd },

        "& .MuiDataGrid-row:hover": {
            backgroundColor: alpha(
                theme.palette.action.hover,
                isDark ? 0.3 : 0.6
            ),
        },
        "& .MuiDataGrid-row.Mui-selected": {
            backgroundColor:
                alpha(theme.palette.primary.main, 0.18) + " !important",
        },

        "& .over-row": { backgroundColor: overTint + " !important" },
        "& .under-row": { backgroundColor: underTint + " !important" },
        "& .has-image-row": {
            borderLeft: `4px solid ${theme.palette.info.main}`
        },
    };
});

/* ========================= COLORS / GLOWS ========================= */
const SERIES_COLORS = [
    "#FF0000", "#00AA88", "#FFD700", "#1E90FF", "#FF1493",
    "#ADFF2F", "#FF7F00", "#8A2BE2", "#FFFF00", "#00CED1",
];

const OVERPRICED_COLOR = "#DC2626";
const UNDERPRICED_COLOR = "#059669";
const AGGREGATE_COLOR = "#7C4DFF";

const cardGlow = (theme, color) => ({
    position: "relative",
    background:
        theme.palette.mode === "dark" ? alpha(color, 0.08) : alpha(color, 0.06),
    color: theme.palette.text.primary,
    border: `1px solid ${alpha(color, 0.6)}`,
    borderRadius: theme.radii?.md || 8,
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    boxShadow: `
        0 0 0.5rem ${alpha(color, 0.5)},
        0 0 1.25rem ${alpha(color, 0.35)},
        inset 0 0 0.7rem ${alpha(color, 0.25)}
    `,
    '&:hover': {
        transform: 'translateY(-2px)',
        boxShadow: `
            0 0 1rem ${alpha(color, 0.6)},
            0 0 2rem ${alpha(color, 0.4)},
            inset 0 0 1rem ${alpha(color, 0.3)}
        `,
    },
});

/* ============================= ENHANCED EXCEL REPORT WITH CHARTS ============================= */
const generateEnhancedVendorReport = async (vendorData, schoolYear, schools) => {
    const {
        vendorName,
        totalSales,
        totalOverpriced,
        totalUnderpriced,
        netVariance,
        overpricedCount,
        underpricedCount,
        totalItems,
        accuracyScore,
        invoices,
        overpricedItems,
        underpricedItems,
        nonBidItems = [],
        schoolBreakdown = []
    } = vendorData;

    const wb = XLSX.utils.book_new();

    // ============================= EXECUTIVE DASHBOARD SHEET =============================
    const dashboardData = [
        ["", "", "", "", "", "", "", "", ""],
        ["", "🎯 VENDOR PERFORMANCE EXECUTIVE DASHBOARD", "", "", "", "", "", "", ""],
        ["", "", "", "", "", "", "", "", ""],
        ["", "📊 CRITICAL METRICS", "", "📈 FINANCIAL IMPACT", "", "🏆 PERFORMANCE SCORE", "", "📅 REPORTING PERIOD", ""],
        ["", "", "", "", "", "", "", "", ""],
        ["", "Vendor Name:", vendorName, "Total Sales Volume:", fmtUSD(totalSales), "Accuracy Score:", fmtPercent(accuracyScore), "School Year:", schoolYear],
        ["", "Report Date:", new Date().toLocaleDateString(), "Net Variance Impact:", fmtUSD(netVariance), "Performance Rating:",
            accuracyScore >= 0.95 ? "⭐⭐⭐⭐⭐ EXCELLENT" :
                accuracyScore >= 0.90 ? "⭐⭐⭐⭐ GOOD" :
                    accuracyScore >= 0.80 ? "⭐⭐⭐ FAIR" :
                        accuracyScore >= 0.70 ? "⭐⭐ POOR" : "⭐ CRITICAL",
            "Total Invoices:", invoices.length],
        ["", "", "", "", "", "", "", "", ""],
        ["", "💸 VARIANCE BREAKDOWN", "", "", "", "", "", "", ""],
        ["", "", "", "", "", "", "", "", ""],
        ["", "Overpriced Items:", overpricedCount.toString(), "Amount Lost:", fmtUSD(totalOverpriced), "Avg Overcharge:", fmtUSD(overpricedCount > 0 ? totalOverpriced / overpricedCount : 0), "", ""],
        ["", "Underpriced Items:", underpricedCount.toString(), "Amount Saved:", fmtUSD(totalUnderpriced), "Avg Savings:", fmtUSD(underpricedCount > 0 ? totalUnderpriced / underpricedCount : 0), "", ""],
        ["", "Off-Bid Items:", nonBidItems.length.toString(), "Risk Exposure:", fmtUSD(nonBidItems.reduce((sum, item) => sum + (item.ItemAmount || 0), 0)), "Compliance Risk:", nonBidItems.length > 0 ? "⚠️ HIGH" : "✅ LOW", "", ""],
        ["", "", "", "", "", "", "", "", ""],
        ["", "📈 KEY PERFORMANCE INDICATORS", "", "", "", "", "", "", ""],
        ["", "", "", "", "", "", "", "", ""],
        ["", "Variance Rate:", fmtPercent(totalItems > 0 ? (overpricedCount + underpricedCount) / totalItems : 0), "Overcharge Rate:", fmtPercent(totalItems > 0 ? overpricedCount / totalItems : 0), "Savings Rate:", fmtPercent(totalItems > 0 ? underpricedCount / totalItems : 0), "", "", ""],
        ["", "Financial Impact:", totalSales > 0 ? fmtPercent(Math.abs(netVariance) / totalSales) : "0%", "Risk Level:",
            Math.abs(netVariance / totalSales) > 0.05 ? "🔴 HIGH" :
                Math.abs(netVariance / totalSales) > 0.02 ? "🟡 MEDIUM" : "🟢 LOW", "", "", "", ""],
        ["", "", "", "", "", "", "", "", ""],
        ["", "🎯 SCHOOL NUTRITION DEPARTMENT RECOMMENDATIONS", "", "", "", "", "", "", ""],
        ["", "", "", "", "", "", "", "", ""],
        ["", "📋 PUBLIC AGENCY ACCOUNTABILITY ACTIONS:", "", "", "", "", "", "", ""],
        ["", "", "", "", "", "", "", "", ""],
        ["", "🔴 IMMEDIATE CREDIT RECOVERY (PUBLIC FUNDS PROTECTION):", "", "", "", "", "", "", ""],
        ["", `• Request FULL CREDIT of ${fmtUSD(totalOverpriced)} for all ${overpricedCount} overpriced items`, "", "", "", "", "", "", ""],
        ["", "• As a public agency, ALL overcharges must be recovered per fiscal responsibility", "", "", "", "", "", "", ""],
        ["", "• Document all overcharges for audit trail and board reporting", "", "", "", "", "", "", ""],
        ["", "", "", "", "", "", "", "", ""],
        ["", "🤝 VENDOR PARTNERSHIP TRANSPARENCY:", "", "", "", "", "", "", ""],
        ["", `• INFORM vendor of ${fmtUSD(totalUnderpriced)} in underpayments (${underpricedCount} items)`, "", "", "", "", "", "", ""],
        ["", "• Deduct underpayments from credit request for fair accounting", "", "", "", "", "", "", ""],
        ["", `• Net credit due to district: ${fmtUSD(Math.max(0, totalOverpriced - totalUnderpriced))}`, "", "", "", "", "", "", ""],
        ["", "• Maintain transparent partnership with honest reporting both ways", "", "", "", "", "", "", ""],
        ["", "", "", "", "", "", "", "", ""],
        ["", "⚠️ COMPLIANCE & GOVERNANCE:", "", "", "", "", "", "", ""],
        ["", `• Investigate ${nonBidItems.length} off-bid items for procurement compliance`, "", "", "", "", "", "", ""],
        ["", "• Report findings to nutrition director and superintendent", "", "", "", "", "", "", ""],
        ["", "• Update vendor performance metrics for future contract decisions", "", "", "", "", "", "", ""],
    ];

    const dashboardWS = XLSX.utils.aoa_to_sheet(dashboardData);

    // Enhanced formatting for dashboard
    dashboardWS['!merges'] = [
        { s: { r: 1, c: 1 }, e: { r: 1, c: 8 } },
        { s: { r: 3, c: 1 }, e: { r: 3, c: 2 } },
        { s: { r: 3, c: 3 }, e: { r: 3, c: 4 } },
        { s: { r: 3, c: 5 }, e: { r: 3, c: 6 } },
        { s: { r: 8, c: 1 }, e: { r: 8, c: 8 } },
        { s: { r: 14, c: 1 }, e: { r: 14, c: 8 } },
        { s: { r: 19, c: 1 }, e: { r: 19, c: 8 } },
    ];

    dashboardWS['!cols'] = [
        { wch: 3 }, { wch: 25 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 20 }, { wch: 15 }, { wch: 10 }
    ];

    // Add conditional formatting for critical values
    dashboardWS['!rows'] = [];
    for (let i = 0; i < 25; i++) {
        dashboardWS['!rows'][i] = { hpt: i === 1 ? 25 : 20 };
    }

    XLSX.utils.book_append_sheet(wb, dashboardWS, "🎯 Executive Dashboard");

    // ============================= INVOICE IMAGE LINKS SHEET =============================
    const invoiceImageData = [
        ["", "", "", "", "", ""],
        ["", "📷 INVOICE IMAGE LINKS & DOCUMENTATION", "", "", "", ""],
        ["", "", "", "", "", ""],
        ["", "Invoice Number", "Invoice Date", "School", "Total Amount", "Image URL"],
        ["", "", "", "", "", ""],
    ];

    // Add invoices with image links
    invoices.forEach(invoice => {
        const imageUrl = invoice.imageURL || "";
        const hasImage = imageUrl.trim() !== "";

        invoiceImageData.push([
            "",
            invoice.InvoiceNumber || "N/A",
            invoice.InvoiceDate ? new Date(invoice.InvoiceDate).toLocaleDateString() : "N/A",
            invoice.SchoolName || "Unknown",
            Number(invoice.InvoiceTotal || 0),
            hasImage ? imageUrl : "No image available"
        ]);
    });

    const invoiceImageWS = XLSX.utils.aoa_to_sheet(invoiceImageData);
    invoiceImageWS['!cols'] = [
        { wch: 3 }, { wch: 20 }, { wch: 12 }, { wch: 25 }, { wch: 15 }, { wch: 80 }
    ];

    // Format currency column
    const invoiceRange = XLSX.utils.decode_range(invoiceImageWS['!ref']);
    for (let R = 5; R <= invoiceRange.e.r; ++R) {
        const cell_ref = 'E' + (R + 1);
        if (invoiceImageWS[cell_ref] && typeof invoiceImageWS[cell_ref].v === 'number') {
            invoiceImageWS[cell_ref].z = '"$"#,##0.00';
        }
        // Make URLs clickable links
        const urlRef = 'F' + (R + 1);
        if (invoiceImageWS[urlRef] && invoiceImageWS[urlRef].v && invoiceImageWS[urlRef].v !== "No image available") {
            invoiceImageWS[urlRef].l = { Target: invoiceImageWS[urlRef].v };
        }
    }

    XLSX.utils.book_append_sheet(wb, invoiceImageWS, "📷 Invoice Images");

    // ============================= VARIANCE ANALYSIS WITH CHARTS SHEET =============================
    const chartAnalysisData = [
        ["", "", "", "", "", "", "", ""],
        ["", "📊 VARIANCE ANALYSIS & TRENDS", "", "", "", "", "", ""],
        ["", "", "", "", "", "", "", ""],
        ["", "🏆 TOP 10 HIGHEST OVERCHARGES", "", "", "", "", "", ""],
        ["", "Rank", "Product Code", "Description", "Unit Overcharge", "Total Impact", "School", "Image URL"],
    ];

    // Add top 10 overpriced items with image links
    const topOverpriced = overpricedItems
        .sort((a, b) => Number(b.OverpricedAmount || 0) - Number(a.OverpricedAmount || 0))
        .slice(0, 10);

    topOverpriced.forEach((item, idx) => {
        chartAnalysisData.push([
            "",
            (idx + 1).toString(),
            item.ProductCode || "N/A",
            (item.ItemDescription || "").substring(0, 40),
            Number(item.VariancePerUnit || 0),
            Number(item.OverpricedAmount || 0),
            item.SchoolName || "Unknown",
            item.imageURL || "No image"
        ]);
    });

    chartAnalysisData.push(["", "", "", "", "", "", "", ""]);
    chartAnalysisData.push(["", "💰 TOP 10 BEST SAVINGS", "", "", "", "", "", ""]);
    chartAnalysisData.push(["", "Rank", "Product Code", "Description", "Unit Savings", "Total Saved", "School", "Image URL"]);

    // Add top 10 underpriced items with image links
    const topUnderpriced = underpricedItems
        .sort((a, b) => Number(b.UnderpricedAmount || 0) - Number(a.UnderpricedAmount || 0))
        .slice(0, 10);

    topUnderpriced.forEach((item, idx) => {
        chartAnalysisData.push([
            "",
            (idx + 1).toString(),
            item.ProductCode || "N/A",
            (item.ItemDescription || "").substring(0, 40),
            Math.abs(Number(item.VariancePerUnit || 0)),
            Number(item.UnderpricedAmount || 0),
            item.SchoolName || "Unknown",
            item.imageURL || "No image"
        ]);
    });

    const chartWS = XLSX.utils.aoa_to_sheet(chartAnalysisData);
    chartWS['!cols'] = [
        { wch: 3 }, { wch: 6 }, { wch: 15 }, { wch: 40 }, { wch: 15 }, { wch: 15 }, { wch: 25 }, { wch: 60 }
    ];

    // Format currency columns and add clickable links for images
    const chartRange = XLSX.utils.decode_range(chartWS['!ref']);
    for (let R = 5; R < chartRange.e.r + 1; ++R) {
        ['E', 'F'].forEach(col => {
            const cell_ref = col + (R + 1);
            if (chartWS[cell_ref] && typeof chartWS[cell_ref].v === 'number') {
                chartWS[cell_ref].z = '"$"#,##0.00';
            }
        });
        // Make image URLs clickable
        const imgRef = 'H' + (R + 1);
        if (chartWS[imgRef] && chartWS[imgRef].v && chartWS[imgRef].v !== "No image") {
            chartWS[imgRef].l = { Target: chartWS[imgRef].v };
        }
    }

    XLSX.utils.book_append_sheet(wb, chartWS, "📊 Analysis & Rankings");

    // ============================= SCHOOL PERFORMANCE MATRIX ============================= */
    if (schoolBreakdown && schoolBreakdown.length > 0) {
        const schoolMatrixData = [
            ["", "", "", "", "", "", "", ""],
            ["", "🏫 SCHOOL-BY-SCHOOL PERFORMANCE MATRIX", "", "", "", "", "", ""],
            ["", "", "", "", "", "", "", ""],
            ["", "School Name", "Total Items", "Overpriced Items", "Underpriced Items", "Net Impact", "Accuracy Score", "Risk Level"],
            ["", "", "", "", "", "", "", ""],
        ];

        schoolBreakdown.forEach(school => {
            const accuracy = school.totalItems > 0 ? ((school.totalItems - school.varianceCount) / school.totalItems) : 1;
            const riskLevel = accuracy >= 0.95 ? "🟢 LOW" : accuracy >= 0.85 ? "🟡 MEDIUM" : "🔴 HIGH";

            schoolMatrixData.push([
                "",
                school.schoolName || "Unknown School",
                school.totalItems || 0,
                school.overpricedCount || 0,
                school.underpricedCount || 0,
                school.netVariance || 0,
                accuracy,
                riskLevel
            ]);
        });

        const schoolMatrixWS = XLSX.utils.aoa_to_sheet(schoolMatrixData);
        const schoolRange = XLSX.utils.decode_range(schoolMatrixWS['!ref']);

        // Format currency and percentage columns
        for (let R = 5; R < schoolRange.e.r + 1; ++R) {
            ['F'].forEach(col => {
                const cell_ref = col + (R + 1);
                if (schoolMatrixWS[cell_ref] && typeof schoolMatrixWS[cell_ref].v === 'number') {
                    schoolMatrixWS[cell_ref].z = '"$"#,##0.00';
                }
            });
            const pctRef = 'G' + (R + 1);
            if (schoolMatrixWS[pctRef] && typeof schoolMatrixWS[pctRef].v === 'number') {
                schoolMatrixWS[pctRef].z = '0.0%';
            }
        }

        schoolMatrixWS['!cols'] = [
            { wch: 3 }, { wch: 30 }, { wch: 12 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 12 }
        ];
        XLSX.utils.book_append_sheet(wb, schoolMatrixWS, "🏫 School Matrix");
    }

    // ============================= DETAILED TRANSACTION LOGS ============================= */
    // Overpriced Items with Enhanced Details and Images
    if (overpricedItems && overpricedItems.length > 0) {
        const overpricedHeaders = [
            "🔴 OVERPRICED TRANSACTIONS - IMMEDIATE ACTION REQUIRED",
            "", "", "", "", "", "", "", "", "", "", "", ""
        ];

        const overpricedSubHeaders = [
            "Invoice Date", "Invoice #", "School", "Product Code", "Description",
            "Qty", "Unit Price", "Contract Price", "Unit Variance", "Total Loss",
            "Severity", "Action Required", "Image URL"
        ];

        const overpricedData = [
            overpricedHeaders,
            ["", "", "", "", "", "", "", "", "", "", "", "", ""],
            overpricedSubHeaders,
            ["", "", "", "", "", "", "", "", "", "", "", "", ""],
            ...overpricedItems.map(item => {
                const variance = Number(item.VariancePerUnit || 0);
                const severity = variance > 10 ? "🔴 CRITICAL" : variance > 5 ? "🟠 HIGH" : variance > 1 ? "🟡 MEDIUM" : "🟢 LOW";
                const action = variance > 10 ? "IMMEDIATE CREDIT REQUEST" : variance > 5 ? "ESCALATE TO VENDOR" : "MONITOR FUTURE ORDERS";

                return [
                    item.InvoiceDate ? new Date(item.InvoiceDate).toLocaleDateString() : "",
                    item.InvoiceNumber || "",
                    item.SchoolName || "Unknown",
                    item.ProductCode || "",
                    item.ItemDescription || "",
                    Number(item.Quantity || 1),
                    Number(item.UnitPrice || 0),
                    Number(item.ContractPrice || 0),
                    variance,
                    Number(item.OverpricedAmount || 0),
                    severity,
                    action,
                    item.imageURL || "No image available"
                ];
            })
        ];

        const overpricedWS = XLSX.utils.aoa_to_sheet(overpricedData);
        overpricedWS['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 12 } }];

        const overpricedRange = XLSX.utils.decode_range(overpricedWS['!ref']);
        for (let R = 4; R <= overpricedRange.e.r; ++R) {
            ['G', 'H', 'I', 'J'].forEach(col => {
                const cell_ref = col + (R + 1);
                if (overpricedWS[cell_ref] && typeof overpricedWS[cell_ref].v === 'number') {
                    overpricedWS[cell_ref].z = '"$"#,##0.00';
                }
            });
            // Make image URLs clickable
            const imgRef = 'M' + (R + 1);
            if (overpricedWS[imgRef] && overpricedWS[imgRef].v && overpricedWS[imgRef].v !== "No image available") {
                overpricedWS[imgRef].l = { Target: overpricedWS[imgRef].v };
            }
        }

        overpricedWS['!cols'] = [
            { wch: 12 }, { wch: 15 }, { wch: 20 }, { wch: 15 }, { wch: 40 }, { wch: 8 },
            { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 15 }, { wch: 25 }, { wch: 60 }
        ];

        XLSX.utils.book_append_sheet(wb, overpricedWS, "🔴 Overpriced Details");
    }

    // Underpriced Items (Savings Opportunities) with Images
    if (underpricedItems && underpricedItems.length > 0) {
        const underpricedHeaders = [
            "🟢 COST SAVINGS ACHIEVED - LEVERAGE IN NEGOTIATIONS",
            "", "", "", "", "", "", "", "", "", "", "", ""
        ];

        const underpricedSubHeaders = [
            "Invoice Date", "Invoice #", "School", "Product Code", "Description",
            "Qty", "Unit Price", "Contract Price", "Unit Savings", "Total Saved",
            "Opportunity", "Negotiation Strategy", "Image URL"
        ];

        const underpricedData = [
            underpricedHeaders,
            ["", "", "", "", "", "", "", "", "", "", "", "", ""],
            underpricedSubHeaders,
            ["", "", "", "", "", "", "", "", "", "", "", "", ""],
            ...underpricedItems.map(item => {
                const savings = Math.abs(Number(item.VariancePerUnit || 0));
                const opportunity = savings > 10 ? "💎 EXCELLENT" : savings > 5 ? "🏆 GOOD" : savings > 1 ? "👍 FAIR" : "✓ MINIMAL";
                const strategy = savings > 10 ? "REQUEST PERMANENT PRICING" : savings > 5 ? "NEGOTIATE VOLUME DISCOUNT" : "MONITOR FOR TRENDS";

                return [
                    item.InvoiceDate ? new Date(item.InvoiceDate).toLocaleDateString() : "",
                    item.InvoiceNumber || "",
                    item.SchoolName || "Unknown",
                    item.ProductCode || "",
                    item.ItemDescription || "",
                    Number(item.Quantity || 1),
                    Number(item.UnitPrice || 0),
                    Number(item.ContractPrice || 0),
                    savings,
                    Number(item.UnderpricedAmount || 0),
                    opportunity,
                    strategy,
                    item.imageURL || "No image available"
                ];
            })
        ];

        const underpricedWS = XLSX.utils.aoa_to_sheet(underpricedData);
        underpricedWS['!merges'] = [{ s: { r: 0, c: 0 }, e: { r: 0, c: 12 } }];

        const underpricedRange = XLSX.utils.decode_range(underpricedWS['!ref']);
        for (let R = 4; R <= underpricedRange.e.r; ++R) {
            ['G', 'H', 'I', 'J'].forEach(col => {
                const cell_ref = col + (R + 1);
                if (underpricedWS[cell_ref] && typeof underpricedWS[cell_ref].v === 'number') {
                    underpricedWS[cell_ref].z = '"$"#,##0.00';
                }
            });
            // Make image URLs clickable
            const imgRef = 'M' + (R + 1);
            if (underpricedWS[imgRef] && underpricedWS[imgRef].v && underpricedWS[imgRef].v !== "No image available") {
                underpricedWS[imgRef].l = { Target: underpricedWS[imgRef].v };
            }
        }

        underpricedWS['!cols'] = [
            { wch: 12 }, { wch: 15 }, { wch: 20 }, { wch: 15 }, { wch: 40 }, { wch: 8 },
            { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 15 }, { wch: 25 }, { wch: 60 }
        ];

        XLSX.utils.book_append_sheet(wb, underpricedWS, "🟢 Savings Achieved");
    }

    // ============================= ACTION PLAN SHEET ============================= */
    const actionPlanData = [
        ["", "", "", "", "", ""],
        ["", "📋 90-DAY SCHOOL NUTRITION ACTION PLAN", "", "", "", ""],
        ["", "", "", "", "", ""],
        ["", "🎯 IMMEDIATE ACTIONS (0-30 DAYS) - PUBLIC FUNDS PROTECTION", "", "", "", ""],
        ["", "", "", "", "", ""],
        ["", "Priority", "Action Item", "Expected Impact", "Responsible Party", "Timeline"],
        ["", "", "", "", "", ""],
        ["", "🔴 HIGH", `Request FULL CREDIT for ALL ${overpricedCount} overpriced items`, fmtUSD(totalOverpriced), "Nutrition Director", "1 week"],
        ["", "🔴 HIGH", `Document vendor notification of ${fmtUSD(totalUnderpriced)} underpayments`, "Transparent partnership", "Business Manager", "1 week"],
        ["", "🔴 HIGH", `Calculate net credit: ${fmtUSD(Math.max(0, totalOverpriced - totalUnderpriced))}`, "Fair accounting", "Finance Team", "2 weeks"],
        ["", "🟠 MEDIUM", `Audit ${nonBidItems.length} off-bid items for compliance`, "Procurement oversight", "Procurement Officer", "3 weeks"],
        ["", "🟡 LOW", `Report findings to superintendent/board`, "Public accountability", "Nutrition Director", "4 weeks"],
        ["", "", "", "", "", ""],
        ["", "📊 MID-TERM ACTIONS (30-60 DAYS) - PROCESS IMPROVEMENT", "", "", "", ""],
        ["", "", "", "", "", ""],
        ["", "🔴 HIGH", "Implement bi-weekly invoice variance monitoring", "Prevent future overcharges", "Nutrition Staff", "6 weeks"],
        ["", "🔴 HIGH", "Establish vendor scorecard with transparency metrics", "Performance tracking", "Procurement Team", "8 weeks"],
        ["", "🟠 MEDIUM", "Review contract terms for credit/adjustment clauses", "Legal protection", "Legal/Procurement", "6 weeks"],
        ["", "🟡 LOW", "Train staff on bid compliance verification", "Prevention focus", "Nutrition Director", "8 weeks"],
        ["", "", "", "", "", ""],
        ["", "🎯 STRATEGIC ACTIONS (60-90 DAYS) - PARTNERSHIP OPTIMIZATION", "", "", "", ""],
        ["", "", "", "", "", ""],
        ["", "🔴 HIGH", "Quarterly vendor performance reviews", "Ongoing accountability", "Nutrition Director", "12 weeks"],
        ["", "🟠 MEDIUM", "Implement real-time pricing validation system", "Automated compliance", "IT/Nutrition", "10 weeks"],
        ["", "🟠 MEDIUM", "Develop mutual adjustment protocol with vendors", "Fair partnership", "Business Manager", "8 weeks"],
        ["", "🟡 LOW", "Board presentation on vendor performance program", "Public transparency", "Superintendent", "12 weeks"],
        ["", "", "", "", "", ""],
        ["", "💡 SCHOOL NUTRITION BEST PRACTICES:", "", "", "", ""],
        ["", "• Always request full credits - public funds require 100% accountability", "", "", "", ""],
        ["", "• Maintain vendor transparency - inform of both over and under charges", "", "", "", ""],
        ["", "• Document everything for audit trails and board reporting", "", "", "", ""],
        ["", "• Use vendor partnerships as educational opportunities for compliance", "", "", "", ""],
    ];

    const actionPlanWS = XLSX.utils.aoa_to_sheet(actionPlanData);
    actionPlanWS['!merges'] = [
        { s: { r: 1, c: 1 }, e: { r: 1, c: 5 } },
        { s: { r: 3, c: 1 }, e: { r: 3, c: 5 } },
        { s: { r: 11, c: 1 }, e: { r: 11, c: 5 } },
        { s: { r: 17, c: 1 }, e: { r: 17, c: 5 } },
    ];

    actionPlanWS['!cols'] = [
        { wch: 3 }, { wch: 12 }, { wch: 40 }, { wch: 25 }, { wch: 20 }, { wch: 15 }
    ];

    XLSX.utils.book_append_sheet(wb, actionPlanWS, "📋 Action Plan");

    const safeVendorName = vendorName.replace(/[^a-zA-Z0-9]/g, '_');
    const timestamp = new Date().toISOString().slice(0, 10);
    const filename = `Enhanced_Vendor_Analysis_${safeVendorName}_${schoolYear}_${timestamp}.xlsx`;

    XLSX.writeFile(wb, filename);
    return filename;
};

/* ============================= MAIN COMPONENT ============================= */
const BidComparisonPage = () => {
    const theme = useTheme();

    // Light/dark aware page background
    const pageBackground =
        theme.palette.mode === "dark"
            ? "linear-gradient(180deg, #020617 0%, #0f172a 100%)"
            : "linear-gradient(180deg, #FFFFFF 0%, #F8F9FA 100%)";

    // State - using views now
    const [masterItems, setMasterItems] = useState([]);
    const [overallStats, setOverallStats] = useState(null);
    const [vendorStats, setVendorStats] = useState([]);
    const [schools, setSchools] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const [mediaViewerOpen, setMediaViewerOpen] = useState(false);
    const [mediaUrl, setMediaUrl] = useState("");
    const [mediaType, setMediaType] = useState("image");

    const [reportDialogOpen, setReportDialogOpen] = useState(false);
    const [generatingReport, setGeneratingReport] = useState(false);

    const [filterReviewed, setFilterReviewed] = useState("notReviewed");
    const [filterVendor, setFilterVendor] = useState("all");
    const [filterSchool, setFilterSchool] = useState("all");
    const [activeTab, setActiveTab] = useState(0);

    const defaultSY = schoolYearOf(new Date());
    const [schoolYear, setSchoolYear] = useState(defaultSY);

    const primaryColor = theme.colors?.primary || theme.palette.primary.main;
    const secondaryColor = theme.colors?.secondary || theme.palette.secondary.main;

    /* =================== FETCH DATA FROM VIEWS =================== */
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                setError(null);

                console.log("🔍 Fetching data from SQL views...");

                const [masterRes, overallRes, vendorRes, schoolsRes] = await Promise.all([
                    axios.get(API_INVOICE_ITEMS_MASTER),
                    axios.get(API_INVOICE_STATS_OVERALL),
                    axios.get(API_VENDOR_VARIANCE_ANALYSIS),
                    axios.get(API_SCHOOLMST),
                ]);

                console.log("📦 Data received from views:");
                console.log("- Master Items:", masterRes.data?.length || 0);
                console.log("- Overall Stats:", overallRes.data?.[0]);
                console.log("- Vendor Stats:", vendorRes.data?.length || 0);
                console.log("- Schools:", schoolsRes.data?.length || 0);

                const masterData = masterRes.data || [];
                const overallData = overallRes.data?.[0] || null;
                const vendorData = vendorRes.data || [];
                const schoolsData = schoolsRes.data || [];

                // Process master items to ensure dates are parsed and image availability is noted
                const processedMaster = masterData.map(item => ({
                    ...item,
                    InvoiceDate: item.InvoiceDate ? new Date(item.InvoiceDate) : null,
                    SchoolYear: item.InvoiceDate ? schoolYearOf(new Date(item.InvoiceDate)) : "Unknown",
                    hasImage: item.imageURL && item.imageURL.trim() !== ""
                }));

                setMasterItems(processedMaster);
                setOverallStats(overallData);
                setVendorStats(vendorData);
                setSchools(schoolsData);

            } catch (err) {
                console.error("❌ Error fetching data from views:", err);
                setError(err.message || "Failed to load data from views");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    /* =================== FILTER DATA BY SCHOOL YEAR =================== */
    const itemsInSchoolYear = useMemo(() => {
        if (!schoolYear || masterItems.length === 0) return [];
        const [start, end] = schoolYearBounds(schoolYear);
        return masterItems.filter(
            (item) => item.InvoiceDate && item.InvoiceDate >= start && item.InvoiceDate <= end
        );
    }, [masterItems, schoolYear]);

    /* =================== APPLY FILTERS =================== */
    const filteredItems = useMemo(() => {
        return itemsInSchoolYear.filter((item) => {
            const reviewedFlag = item.ItemIsReviewed === 1;
            if (filterReviewed === "reviewed" && !reviewedFlag) return false;
            if (filterReviewed === "notReviewed" && reviewedFlag) return false;
            if (filterVendor !== "all" && item.VendorName !== filterVendor) return false;
            if (filterSchool !== "all" && item.SchoolName !== filterSchool) return false;
            return true;
        });
    }, [itemsInSchoolYear, filterReviewed, filterVendor, filterSchool]);

    /* =================== CALCULATE METRICS USING NEW VIEW FIELDS =================== */
    const metrics = useMemo(() => {
        // Using the new view fields for better categorization
        const overpricedItems = filteredItems.filter(item => item.VarianceType === 'Overpriced');
        const underpricedItems = filteredItems.filter(item => item.VarianceType === 'Underpriced');
        const onContractItems = filteredItems.filter(item => item.VarianceType === 'On Contract');
        const notOnBidItems = filteredItems.filter(item => item.VarianceType === 'Not on Bid');

        // CRITICAL: Sum the actual OverpricedAmount and UnderpricedAmount fields from the view
        const totalOverpriced = filteredItems.reduce((sum, item) => {
            return sum + Number(item.OverpricedAmount || 0);
        }, 0);

        const totalUnderpriced = filteredItems.reduce((sum, item) => {
            return sum + Number(item.UnderpricedAmount || 0);
        }, 0);

        const netVariance = totalOverpriced - totalUnderpriced;

        // Calculate total spend from unique invoices
        const invoiceMap = new Map();
        filteredItems.forEach(item => {
            if (item.InvoiceApiId && !invoiceMap.has(item.InvoiceApiId)) {
                invoiceMap.set(item.InvoiceApiId, Number(item.InvoiceTotal || 0));
            }
        });
        const totalSpend = Array.from(invoiceMap.values()).reduce((sum, val) => sum + val, 0);

        // Count items with images
        const itemsWithImages = filteredItems.filter(item => item.hasImage).length;

        return {
            overpricedCount: overpricedItems.length,
            underpricedCount: underpricedItems.length,
            onContractCount: onContractItems.length,
            notOnBidCount: notOnBidItems.length,
            totalOverpriced,
            totalUnderpriced,
            netVariance,
            totalSpend,
            itemsWithImages,
            overpricedItems,
            underpricedItems,
            onContractItems,
            notOnBidItems
        };
    }, [filteredItems]);

    /* =================== VENDOR OPTIONS =================== */
    const vendorOptions = useMemo(
        () => Array.from(new Set(masterItems.map(i => i.VendorName).filter(Boolean))).sort(),
        [masterItems]
    );

    const schoolOptions = useMemo(
        () => Array.from(new Set(masterItems.map(i => i.SchoolName).filter(Boolean))).sort(),
        [masterItems]
    );

    const schoolYearOptions = useMemo(() => {
        const set = new Set(
            masterItems.map((i) => i.SchoolYear).filter((s) => s && s !== "Unknown")
        );
        set.add(defaultSY);
        return Array.from(set).sort((a, b) => a.localeCompare(b));
    }, [masterItems, defaultSY]);

    /* =================== VENDOR ANALYTICS FOR SELECTED YEAR =================== */
    const vendorAnalytics = useMemo(() => {
        return vendorOptions.map(vendorName => {
            const vendorItems = filteredItems.filter(item => item.VendorName === vendorName);
            const overpricedItems = vendorItems.filter(item => item.VarianceType === 'Overpriced');
            const underpricedItems = vendorItems.filter(item => item.VarianceType === 'Underpriced');
            const notOnBidItems = vendorItems.filter(item => item.VarianceType === 'Not on Bid');

            // CRITICAL: Calculate totals from ALL vendor items using view fields
            const totalSales = vendorItems.reduce((sum, item) =>
                sum + (Number(item.UnitPrice || 0) * Number(item.Quantity || 1)), 0);

            const totalOverpriced = vendorItems.reduce((sum, item) => {
                return sum + Number(item.OverpricedAmount || 0);
            }, 0);

            const totalUnderpriced = vendorItems.reduce((sum, item) => {
                return sum + Number(item.UnderpricedAmount || 0);
            }, 0);

            const netVariance = totalOverpriced - totalUnderpriced;

            const accuracyScore = vendorItems.length > 0
                ? (vendorItems.length - overpricedItems.length - notOnBidItems.length) / vendorItems.length
                : 1;

            // Enhanced school breakdown
            const schoolBreakdown = schoolOptions.map(schoolName => {
                const schoolItems = vendorItems.filter(item => item.SchoolName === schoolName);
                const schoolOverpriced = schoolItems.filter(item => item.VarianceType === 'Overpriced');
                const schoolUnderpriced = schoolItems.filter(item => item.VarianceType === 'Underpriced');
                const schoolNotOnBid = schoolItems.filter(item => item.VarianceType === 'Not on Bid');

                return {
                    schoolName,
                    totalItems: schoolItems.length,
                    overpricedCount: schoolOverpriced.length,
                    underpricedCount: schoolUnderpriced.length,
                    varianceCount: schoolOverpriced.length + schoolUnderpriced.length,
                    netVariance: schoolOverpriced.reduce((sum, item) => sum + Number(item.OverpricedAmount || 0), 0) -
                        schoolUnderpriced.reduce((sum, item) => sum + Number(item.UnderpricedAmount || 0), 0),
                    nonBidCount: schoolNotOnBid.length
                };
            }).filter(school => school.totalItems > 0);

            // Group invoices
            const invoiceMap = new Map();
            vendorItems.forEach(item => {
                const key = `${item.InvoiceApiId}-${item.InvoiceNumber}`;
                if (!invoiceMap.has(key)) {
                    invoiceMap.set(key, {
                        InvoiceApiId: item.InvoiceApiId,
                        InvoiceNumber: item.InvoiceNumber,
                        InvoiceDate: item.InvoiceDate,
                        SchoolName: item.SchoolName,
                        imageURL: item.imageURL,
                        InvoiceTotal: item.InvoiceTotal,
                        items: [],
                        itemCount: 0,
                        overpricedCount: 0,
                        underpricedCount: 0,
                        varianceTotal: 0
                    });
                }
                const inv = invoiceMap.get(key);
                inv.items.push(item);
                inv.itemCount++;
                if (item.VarianceType === 'Overpriced') {
                    inv.overpricedCount++;
                    inv.varianceTotal += Number(item.OverpricedAmount || 0);
                } else if (item.VarianceType === 'Underpriced') {
                    inv.underpricedCount++;
                    inv.varianceTotal -= Number(item.UnderpricedAmount || 0);
                }
            });

            const invoices = Array.from(invoiceMap.values());

            return {
                vendorName,
                totalSales,
                totalOverpriced,
                totalUnderpriced,
                netVariance,
                overpricedCount: overpricedItems.length,
                underpricedCount: underpricedItems.length,
                totalItems: vendorItems.length,
                notOnBidCount: notOnBidItems.length,
                accuracyScore,
                overpricedItems,
                underpricedItems,
                nonBidItems: notOnBidItems,
                schoolBreakdown,
                invoices
            };
        }).filter(v => v.totalItems > 0).sort((a, b) => Math.abs(b.netVariance) - Math.abs(a.netVariance));
    }, [filteredItems, vendorOptions, schoolOptions]);

    /* =================== INVOICE GROUPS =================== */
    const invoiceGroups = useMemo(() => {
        const map = new Map();
        [...metrics.overpricedItems, ...metrics.underpricedItems].forEach((item) => {
            const key = `${item.VendorName}-${item.InvoiceNumber}-${item.InvoiceApiId}`;
            if (!map.has(key)) {
                map.set(key, {
                    id: key,
                    invoiceApiId: item.InvoiceApiId,
                    InvoiceNumber: item.InvoiceNumber,
                    VendorName: item.VendorName,
                    SchoolName: item.SchoolName,
                    InvoiceDate: item.InvoiceDate,
                    imageURL: item.imageURL,
                    InvoiceTotal: item.InvoiceTotal,
                    hasImage: item.hasImage,
                    items: [],
                });
            }
            map.get(key).items.push(item);
        });

        return Array.from(map.values()).map(inv => {
            const allItemsForInvoice = filteredItems.filter(
                item => item.InvoiceApiId === inv.invoiceApiId
            );

            const overpricedInvoiceItems = inv.items.filter(item => item.VarianceType === 'Overpriced');
            const underpricedInvoiceItems = inv.items.filter(item => item.VarianceType === 'Underpriced');

            // Calculate totals using the correct view fields
            const overpricedTotal = inv.items.reduce((sum, item) => {
                return sum + Number(item.OverpricedAmount || 0);
            }, 0);

            const underpricedTotal = inv.items.reduce((sum, item) => {
                return sum + Number(item.UnderpricedAmount || 0);
            }, 0);

            return {
                ...inv,
                itemCount: allItemsForInvoice.length,
                overpricedCount: overpricedInvoiceItems.length,
                underpricedCount: underpricedInvoiceItems.length,
                scannedTotal: inv.items.reduce((sum, item) =>
                    sum + (Number(item.UnitPrice || 0) * Number(item.Quantity || 1)), 0),
                contractTotal: inv.items.reduce((sum, item) =>
                    sum + (Number(item.ContractPrice || 0) * Number(item.Quantity || 1)), 0),
                overpricedTotal: overpricedTotal,
                underpricedTotal: underpricedTotal,
                netVarianceTotal: overpricedTotal - underpricedTotal
            };
        }).filter(inv => inv.overpricedCount > 0 || inv.underpricedCount > 0)
            .sort((a, b) => (b.InvoiceDate || 0) - (a.InvoiceDate || 0));
    }, [metrics.overpricedItems, metrics.underpricedItems, filteredItems]);

    /* =================== CHART DATA =================== */
    const vendorColor = (name) => {
        const idx = vendorOptions.findIndex((v) => v === name);
        if (idx < 0) return SERIES_COLORS[0];
        return SERIES_COLORS[idx % SERIES_COLORS.length];
    };

    const lineChartData = useMemo(() => {
        if (!schoolYear || itemsInSchoolYear.length === 0) return [];

        const [start, end] = schoolYearBounds(schoolYear);
        const days = enumerateDays(start, end);

        const base = days.map((d) => {
            const obj = { date: dateKey(d) };
            vendorOptions.forEach((v) => {
                obj[`${v}_overpriced`] = 0;
                obj[`${v}_underpriced`] = 0;
            });
            return obj;
        });

        const idxByDate = new Map(base.map((row, i) => [row.date, i]));

        // Add overpriced items
        metrics.overpricedItems.forEach((item) => {
            const dk = dateKey(item.InvoiceDate);
            const i = idxByDate.get(dk);
            if (i != null && item.VendorName) {
                const key = `${item.VendorName}_overpriced`;
                if (!Object.prototype.hasOwnProperty.call(base[i], key)) {
                    base[i][key] = 0;
                }
                base[i][key] += 1;
            }
        });

        // Add underpriced items
        metrics.underpricedItems.forEach((item) => {
            const dk = dateKey(item.InvoiceDate);
            const i = idxByDate.get(dk);
            if (i != null && item.VendorName) {
                const key = `${item.VendorName}_underpriced`;
                if (!Object.prototype.hasOwnProperty.call(base[i], key)) {
                    base[i][key] = 0;
                }
                base[i][key] += 1;
            }
        });

        return base;
    }, [schoolYear, itemsInSchoolYear, vendorOptions, metrics.overpricedItems, metrics.underpricedItems]);

    /* =================== HANDLERS =================== */
    const handleGenerateReport = async (vendorName) => {
        const vendorData = vendorAnalytics.find(v => v.vendorName === vendorName);
        if (!vendorData) return;

        setGeneratingReport(true);
        try {
            const filename = await generateEnhancedVendorReport(vendorData, schoolYear, schools);
            console.log(`✅ Report generated: ${filename}`);
        } catch (err) {
            console.error("❌ Failed to generate report:", err);
        } finally {
            setGeneratingReport(false);
            setReportDialogOpen(false);
        }
    };

    const handleReviewedToggle = async (apiid, checked) => {
        try {
            await axios.put(`${API_AZ_INVOICEITEMS}/${apiid}`, {
                isReviewed: checked ? 1 : 0,
            });

            setMasterItems((prev) =>
                prev.map((item) =>
                    item.InvoiceItemId === apiid
                        ? { ...item, ItemIsReviewed: checked ? 1 : 0 }
                        : item
                )
            );
        } catch (err) {
            console.error("Error updating isReviewed:", err);
        }
    };

    const handleImageView = (imageUrl, invoiceNumber) => {
        if (!imageUrl || imageUrl.trim() === "") {
            console.log("No image available for invoice:", invoiceNumber);
            return;
        }

        const isPdf = /\.pdf(\?|$)/i.test(imageUrl);
        setMediaUrl(imageUrl);
        setMediaType(isPdf ? "pdf" : "image");
        setMediaViewerOpen(true);
    };

    const handleImageUrlOpen = (imageUrl) => {
        if (imageUrl && imageUrl.trim() !== "") {
            window.open(imageUrl, '_blank');
        }
    };

    /* =================== GRID COLUMNS =================== */
    const itemColumns = [
        {
            field: "ItemIsReviewed",
            headerName: "✓",
            width: 60,
            renderCell: (params) => {
                const reviewedFlag = params.row.ItemIsReviewed === 1;
                return (
                    <Checkbox
                        checked={reviewedFlag}
                        onChange={(e) =>
                            handleReviewedToggle(params.row.InvoiceItemId, e.target.checked)
                        }
                        size="small"
                        sx={{
                            color: theme.palette.action.active,
                            "&.Mui-checked": {
                                color: theme.palette.success.main,
                            },
                        }}
                    />
                );
            },
            sortable: false,
            filterable: false,
        },
        {
            field: "viewInvoice",
            headerName: "📄",
            width: 80,
            renderCell: (params) => {
                const hasImage = params.row.hasImage;
                const imageUrl = params.row.imageURL || "";
                return (
                    <Box display="flex" gap={0.5}>
                        <MuiTooltip title={hasImage ? "View Invoice Image" : "No image available"} arrow>
                            <span>
                                <IconButton
                                    size="small"
                                    onClick={() => handleImageView(imageUrl, params.row.InvoiceNumber)}
                                    disabled={!hasImage}
                                    sx={{
                                        color: hasImage ? theme.palette.info.main : theme.palette.action.disabled,
                                        opacity: hasImage ? 1 : 0.5
                                    }}
                                >
                                    <VisibilityIcon fontSize="small" />
                                </IconButton>
                            </span>
                        </MuiTooltip>
                        {hasImage && (
                            <MuiTooltip title="Open image in new tab" arrow>
                                <IconButton
                                    size="small"
                                    onClick={() => handleImageUrlOpen(imageUrl)}
                                    sx={{ color: theme.palette.primary.main }}
                                >
                                    <OpenInNewIcon fontSize="small" />
                                </IconButton>
                            </MuiTooltip>
                        )}
                    </Box>
                );
            },
            sortable: false,
            filterable: false,
        },
        {
            field: "VarianceType",
            headerName: "Type",
            width: 100,
            renderCell: (params) => {
                const type = params.value;
                const color = type === 'Overpriced' ? 'error' :
                    type === 'Underpriced' ? 'success' :
                        type === 'Not on Bid' ? 'warning' : 'default';
                const icon = type === 'Overpriced' ? '📈' :
                    type === 'Underpriced' ? '📉' :
                        type === 'Not on Bid' ? '🚫' : '✅';

                return (
                    <Box display="flex" alignItems="center" gap={0.5}>
                        <Chip
                            icon={<span style={{ fontSize: '10px' }}>{icon}</span>}
                            label={type.split(' ')[0]}
                            color={color}
                            size="small"
                            sx={{ fontWeight: 600, fontSize: "0.7rem", height: 24 }}
                        />
                        {params.row.hasImage && (
                            <ImageIcon sx={{ fontSize: 16, color: theme.palette.info.main }} />
                        )}
                    </Box>
                );
            },
        },
        { field: "ProductCode", headerName: "Product Code", width: 120 },
        {
            field: "Quantity",
            headerName: "Qty",
            width: 70,
            type: "number",
        },
        {
            field: "UnitPrice",
            headerName: "Unit Price",
            width: 100,
            valueFormatter: (p) => fmtUSD(p.value),
            type: "number",
        },
        {
            field: "ContractPrice",
            headerName: "Contract",
            width: 100,
            valueFormatter: (p) => fmtUSD(p.value),
            type: "number",
        },
        {
            field: "VariancePerUnit",
            headerName: "Variance",
            width: 100,
            valueFormatter: (p) => fmtUSD(p.value),
            type: "number",
            renderCell: (params) => {
                const value = Number(params.value || 0);
                const isOverpriced = value > 0;
                return (
                    <Typography
                        variant="body2"
                        fontWeight={600}
                        color={isOverpriced ? "error.main" : "success.main"}
                        sx={{ fontSize: '0.8rem' }}
                    >
                        {fmtUSD(value)}
                    </Typography>
                );
            },
        },
        {
            field: "varianceAmount",
            headerName: "Total Impact",
            width: 120,
            valueGetter: (params) => {
                return Number(params.row.OverpricedAmount || 0) ||
                    Number(params.row.UnderpricedAmount || 0) || 0;
            },
            valueFormatter: (p) => fmtUSD(p.value),
            type: "number",
            renderCell: (params) => {
                const overpricedAmount = Number(params.row.OverpricedAmount || 0);
                const underpricedAmount = Number(params.row.UnderpricedAmount || 0);
                const amount = overpricedAmount || underpricedAmount;
                const isOverpriced = overpricedAmount > 0;

                return (
                    <Typography
                        variant="body2"
                        fontWeight={700}
                        color={isOverpriced ? "error.main" : "success.main"}
                        sx={{ fontSize: '0.8rem' }}
                    >
                        {isOverpriced ? `+${fmtUSD(amount)}` : `-${fmtUSD(amount)}`}
                    </Typography>
                );
            },
        },
        { field: "ItemDescription", headerName: "Description", flex: 1, minWidth: 200 },
    ];

    const invoiceColumns = [
        {
            field: "email",
            headerName: "📧",
            width: 50,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => (
                <MuiTooltip title="Email variance details" arrow>
                    <IconButton size="small" sx={{ color: theme.palette.warning.main }}>
                        <EmailOutlinedIcon fontSize="small" />
                    </IconButton>
                </MuiTooltip>
            ),
        },
        {
            field: "viewInvoiceImage",
            headerName: "🖼️",
            width: 60,
            sortable: false,
            filterable: false,
            disableColumnMenu: true,
            renderCell: (params) => {
                const hasImage = params.row.hasImage;
                const imageUrl = params.row.imageURL || "";
                return (
                    <MuiTooltip title={hasImage ? "View Invoice Image" : "No image available"} arrow>
                        <span>
                            <IconButton
                                size="small"
                                onClick={() => handleImageView(imageUrl, params.row.InvoiceNumber)}
                                disabled={!hasImage}
                                sx={{
                                    color: hasImage ? theme.palette.info.main : theme.palette.action.disabled,
                                    opacity: hasImage ? 1 : 0.5
                                }}
                            >
                                <ImageIcon fontSize="small" />
                            </IconButton>
                        </span>
                    </MuiTooltip>
                );
            },
        },
        GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        {
            field: "InvoiceDate",
            headerName: "Date",
            width: 100,
            valueFormatter: (p) =>
                p.value ? new Date(p.value).toLocaleDateString() : "",
        },
        { field: "VendorName", headerName: "Vendor", width: 150 },
        { field: "SchoolName", headerName: "School", width: 140 },
        { field: "InvoiceNumber", headerName: "Invoice #", width: 120 },
        {
            field: "itemCount",
            headerName: "Items",
            type: "number",
            width: 80,
        },
        {
            field: "overpricedCount",
            headerName: "Over",
            type: "number",
            width: 70,
            renderCell: (params) => (
                <Chip
                    label={params.value}
                    color="error"
                    size="small"
                    sx={{ fontWeight: 600, height: 20, fontSize: '0.7rem' }}
                />
            ),
        },
        {
            field: "underpricedCount",
            headerName: "Under",
            type: "number",
            width: 70,
            renderCell: (params) => (
                <Chip
                    label={params.value}
                    color="success"
                    size="small"
                    sx={{ fontWeight: 600, height: 20, fontSize: '0.7rem' }}
                />
            ),
        },
        {
            field: "netVarianceTotal",
            headerName: "Net Impact",
            type: "number",
            width: 120,
            valueFormatter: (p) => fmtUSD(p.value),
            renderCell: (params) => {
                const value = Number(params.value || 0);
                const isOverpriced = value > 0;
                return (
                    <Typography
                        variant="body2"
                        fontWeight={700}
                        color={isOverpriced ? "error.main" : "success.main"}
                        sx={{ fontSize: '0.8rem' }}
                    >
                        {isOverpriced ? `+${fmtUSD(value)}` : fmtUSD(value)}
                    </Typography>
                );
            },
        },
    ];

    /* =================== LOADING & ERROR STATES =================== */
    if (loading) {
        return (
            <Box sx={{ minHeight: "100vh", background: pageBackground, p: { xs: 1, md: 2 } }}>
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: theme.radii?.lg || 3,
                        p: { xs: 2, md: 3 },
                        mb: 3,
                        background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                        color: "#fff",
                        textAlign: "center",
                    }}
                >
                    <Typography variant="h4" fontWeight={700} sx={{ mb: 2 }}>
                        📊 Loading Enhanced Bid Comparison Data...
                    </Typography>
                    <LinearProgress sx={{ mt: 2, backgroundColor: alpha("#fff", 0.3) }} />
                </Paper>
            </Box>
        );
    }

    if (error) {
        return (
            <Box sx={{ minHeight: "100vh", background: pageBackground, p: { xs: 1, md: 2 } }}>
                <Paper sx={{ p: 3, bgcolor: theme.palette.error.light, color: theme.palette.error.contrastText, borderRadius: theme.radii?.lg || 3 }}>
                    <Typography variant="h6" gutterBottom>❌ Error Loading Data</Typography>
                    <Typography variant="body1" sx={{ mb: 2 }}>{error}</Typography>
                    <Button
                        variant="contained"
                        startIcon={<RefreshIcon />}
                        onClick={() => window.location.reload()}
                        sx={{ backgroundColor: theme.palette.error.dark }}
                    >
                        Retry Loading
                    </Button>
                </Paper>
            </Box>
        );
    }

    if (masterItems.length === 0) {
        return (
            <Box sx={{ minHeight: "100vh", background: pageBackground, p: { xs: 1, md: 2 } }}>
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: theme.radii?.lg || 3,
                        p: { xs: 2, md: 3 },
                        mb: 3,
                        background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                        color: "#fff",
                        textAlign: "center",
                    }}
                >
                    <Typography variant="h4" fontWeight={700}>
                        📊 Enhanced Bid Comparison Analysis
                    </Typography>
                </Paper>

                <Paper sx={{ p: 4, textAlign: "center", borderRadius: theme.radii?.lg || 3 }}>
                    <ErrorOutlineIcon sx={{ fontSize: 64, color: theme.palette.text.secondary, mb: 2 }} />
                    <Typography variant="h6" gutterBottom>No Data Available</Typography>
                    <Typography variant="body1" color="text.secondary">
                        No bid comparison data found. Please ensure invoices and vendor products are uploaded.
                    </Typography>
                </Paper>
            </Box>
        );
    }

    /* =================== MAIN RENDER =================== */
    return (
        <Box sx={{ minHeight: "100vh", background: pageBackground, p: { xs: 1, md: 2 } }}>
            {/* Header */}
            <Paper
                elevation={0}
                sx={{
                    borderRadius: theme.radii?.lg || 3,
                    p: { xs: 2, md: 3 },
                    mb: 2,
                    background: `linear-gradient(135deg, ${primaryColor}, ${secondaryColor})`,
                    color: "#fff",
                    position: "relative",
                    overflow: "hidden",
                    border: `1px solid ${alpha("#fff", 0.15)}`,
                    boxShadow: `
                        0 25px 50px ${alpha(primaryColor, 0.25)},
                        0 12px 30px ${alpha(primaryColor, 0.15)}
                    `,
                    '&::after': {
                        content: '""',
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: 'linear-gradient(135deg, rgba(255,255,255,0.1) 0%, transparent 50%)',
                        pointerEvents: 'none',
                    },
                }}
            >
                <Grid container alignItems="center" spacing={2}>
                    <Grid item xs={12} md={8}>
                        <Box display="flex" alignItems="center" gap={2} mb={1}>
                            <Box
                                sx={{
                                    width: 48,
                                    height: 48,
                                    borderRadius: 2,
                                    backgroundColor: alpha("#fff", 0.15),
                                    color: "#fff",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    border: `1px solid ${alpha("#fff", 0.2)}`,
                                }}
                            >
                                <TrendingUpIcon sx={{ fontSize: 28 }} />
                            </Box>
                            <Box>
                                <Typography
                                    variant="h4"
                                    fontWeight={800}
                                    sx={{
                                        fontSize: { xs: "1.5rem", md: "2rem" },
                                        lineHeight: 1.1,
                                        textShadow: `0 0 20px ${alpha("#fff", 0.5)}`,
                                    }}
                                >
                                    Enhanced Bid Comparison
                                </Typography>
                                <Typography
                                    variant="subtitle1"
                                    sx={{
                                        opacity: 0.95,
                                        fontWeight: 500,
                                        fontSize: { xs: "0.9rem", md: "1rem" },
                                    }}
                                >
                                    Complete Variance Analysis with Invoice Images: Overpriced, Underpriced & Off-Bid Tracking
                                </Typography>
                            </Box>
                        </Box>
                    </Grid>

                    <Grid item xs={12} md={4}>
                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: { xs: 'row', md: 'column' },
                                alignItems: { xs: "center", md: "flex-end" },
                                gap: 2,
                            }}
                        >
                            <Box
                                sx={{
                                    p: 2,
                                    borderRadius: 2,
                                    backgroundColor: alpha("#000", 0.15),
                                    border: `1px solid ${alpha("#fff", 0.2)}`,
                                    backdropFilter: "blur(10px)",
                                    textAlign: 'center'
                                }}
                            >
                                <Typography variant="caption" sx={{ opacity: 0.8, display: 'block' }}>
                                    School Year
                                </Typography>
                                <Typography variant="h5" fontWeight={700} sx={{ lineHeight: 1 }}>
                                    {schoolYear}
                                </Typography>
                                <Typography variant="caption" sx={{ opacity: 0.8, fontSize: '0.65rem' }}>
                                    {metrics.itemsWithImages} items with images
                                </Typography>
                            </Box>

                            <Button
                                variant="contained"
                                startIcon={<AssessmentIcon />}
                                onClick={() => setReportDialogOpen(true)}
                                disabled={vendorAnalytics.length === 0}
                                size="small"
                                sx={{
                                    backgroundColor: alpha("#000", 0.15),
                                    border: `1px solid ${alpha("#fff", 0.2)}`,
                                    backdropFilter: "blur(10px)",
                                    color: "#fff",
                                    fontWeight: 600,
                                    textTransform: "none",
                                    borderRadius: 2,
                                    '&:hover': {
                                        backgroundColor: alpha("#000", 0.25),
                                        transform: 'translateY(-1px)',
                                    },
                                }}
                            >
                                Generate Report
                            </Button>
                        </Box>
                    </Grid>
                </Grid>
            </Paper>

            {/* Financial Impact Alert */}
            {(metrics.totalOverpriced > 0 || metrics.totalUnderpriced > 0) && (
                <Alert
                    severity={metrics.netVariance > 0 ? "error" : "success"}
                    sx={{
                        mb: 2,
                        p: 3,
                        borderRadius: theme.radii?.lg || 3,
                        boxShadow: `0 8px 24px ${alpha(metrics.netVariance > 0 ? OVERPRICED_COLOR : UNDERPRICED_COLOR, 0.15)}`
                    }}
                >
                    <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                        🏫 School District Financial Impact Summary
                    </Typography>
                    <Grid container spacing={3}>
                        <Grid item xs={12} md={4}>
                            <Box textAlign="center" p={2} sx={{ backgroundColor: alpha(OVERPRICED_COLOR, 0.1), borderRadius: 2 }}>
                                <Typography variant="h4" fontWeight={800} sx={{ color: OVERPRICED_COLOR }}>
                                    {fmtUSD(metrics.totalOverpriced)}
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                    Total Overpayments ({metrics.overpricedCount} items)
                                </Typography>
                                <Typography variant="caption" sx={{ color: OVERPRICED_COLOR, fontWeight: 600 }}>
                                    REQUEST FULL CREDIT
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box textAlign="center" p={2} sx={{ backgroundColor: alpha(UNDERPRICED_COLOR, 0.1), borderRadius: 2 }}>
                                <Typography variant="h4" fontWeight={800} sx={{ color: UNDERPRICED_COLOR }}>
                                    {fmtUSD(metrics.totalUnderpriced)}
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                    Total Underpayments ({metrics.underpricedCount} items)
                                </Typography>
                                <Typography variant="caption" sx={{ color: UNDERPRICED_COLOR, fontWeight: 600 }}>
                                    NOTIFY VENDOR
                                </Typography>
                            </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <Box textAlign="center" p={2} sx={{
                                backgroundColor: alpha(metrics.netVariance >= 0 ? OVERPRICED_COLOR : UNDERPRICED_COLOR, 0.1),
                                borderRadius: 2
                            }}>
                                <Typography variant="h4" fontWeight={800} sx={{
                                    color: metrics.netVariance >= 0 ? OVERPRICED_COLOR : UNDERPRICED_COLOR
                                }}>
                                    {fmtUSD(Math.abs(metrics.netVariance))}
                                </Typography>
                                <Typography variant="body2" fontWeight={600}>
                                    Net Credit Due to District
                                </Typography>
                                <Typography variant="caption" sx={{
                                    color: metrics.netVariance >= 0 ? OVERPRICED_COLOR : UNDERPRICED_COLOR,
                                    fontWeight: 600
                                }}>
                                    {metrics.netVariance >= 0 ? "DISTRICT OWED" : "DISTRICT OWES"}
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </Alert>
            )}

            {/* Horizontal Summary Cards */}
            <Grid container spacing={2} mb={2}>
                <Grid item xs={6} sm={2}>
                    <Card sx={cardGlow(theme, theme.palette.primary.main)}>
                        <CardContent sx={{ textAlign: "center", p: { xs: 1.5, sm: 2 } }}>
                            <AttachMoneyIcon sx={{ fontSize: { xs: 24, sm: 32 }, color: theme.palette.primary.main, mb: 0.5 }} />
                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
                                Total Spend
                            </Typography>
                            {/* TOTAL TEXT NOW USES THEME TEXT COLOR (NO WHITE / SPECIAL COLOR) */}
                            <Typography
                                variant="h6"
                                fontWeight="bold"
                                sx={{ fontSize: { xs: '0.9rem', sm: '1.1rem' } }}
                            >
                                {fmtUSD(metrics.totalSpend)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={6} sm={2}>
                    <Card sx={cardGlow(theme, OVERPRICED_COLOR)}>
                        <CardContent sx={{ textAlign: "center", p: { xs: 1.5, sm: 2 } }}>
                            <TrendingUpIcon sx={{ fontSize: { xs: 24, sm: 32 }, color: OVERPRICED_COLOR, mb: 0.5 }} />
                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
                                Total Overpayments
                            </Typography>
                            <Typography
                                variant="h5"
                                fontWeight="bold"
                                sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                            >
                                {fmtUSD(metrics.totalOverpriced)}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                            >
                                {metrics.overpricedCount} items
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={6} sm={2}>
                    <Card sx={cardGlow(theme, UNDERPRICED_COLOR)}>
                        <CardContent sx={{ textAlign: "center", p: { xs: 1.5, sm: 2 } }}>
                            <TrendingDownIcon sx={{ fontSize: { xs: 24, sm: 32 }, color: UNDERPRICED_COLOR, mb: 0.5 }} />
                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
                                Total Underpayments
                            </Typography>
                            <Typography
                                variant="h5"
                                fontWeight="bold"
                                sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                            >
                                {fmtUSD(metrics.totalUnderpriced)}
                            </Typography>
                            <Typography
                                variant="caption"
                                sx={{ fontSize: { xs: '0.65rem', sm: '0.75rem' } }}
                            >
                                {metrics.underpricedCount} items
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={6} sm={2}>
                    <Card sx={cardGlow(theme, theme.palette.success.main)}>
                        <CardContent sx={{ textAlign: "center", p: { xs: 1.5, sm: 2 } }}>
                            <CheckCircleOutlineIcon sx={{ fontSize: { xs: 24, sm: 32 }, color: theme.palette.success.main, mb: 0.5 }} />
                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
                                On Contract
                            </Typography>
                            <Typography
                                variant="h6"
                                fontWeight="bold"
                                sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}
                            >
                                {metrics.onContractCount}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={6} sm={2}>
                    <Card sx={cardGlow(theme, theme.palette.warning.main)}>
                        <CardContent sx={{ textAlign: "center", p: { xs: 1.5, sm: 2 } }}>
                            <SchoolIcon sx={{ fontSize: { xs: 24, sm: 32 }, color: theme.palette.warning.main, mb: 0.5 }} />
                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
                                Off-Bid
                            </Typography>
                            <Typography
                                variant="h6"
                                fontWeight="bold"
                                sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}
                            >
                                {metrics.notOnBidCount}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>

                <Grid item xs={6} sm={2}>
                    <Card sx={cardGlow(theme, AGGREGATE_COLOR)}>
                        <CardContent sx={{ textAlign: "center", p: { xs: 1.5, sm: 2 } }}>
                            {metrics.netVariance >= 0 ? (
                                <MoneyOffIcon sx={{ fontSize: { xs: 24, sm: 32 }, color: OVERPRICED_COLOR, mb: 0.5 }} />
                            ) : (
                                <SavingsIcon sx={{ fontSize: { xs: 24, sm: 32 }, color: UNDERPRICED_COLOR, mb: 0.5 }} />
                            )}
                            <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, mb: 0.5 }}>
                                Net Impact
                            </Typography>
                            <Typography
                                variant="h6"
                                fontWeight="bold"
                                sx={{ fontSize: { xs: '0.8rem', sm: '1rem' } }}
                            >
                                {metrics.netVariance >= 0
                                    ? `+${fmtUSD(metrics.netVariance)}`
                                    : fmtUSD(metrics.netVariance)}
                            </Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            {/* Charts and Filters Row */}
            <Grid container spacing={2} mb={2}>
                {/* Filters */}
                <Grid item xs={12} lg={4}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            borderRadius: theme.radii?.lg || 3,
                            border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                            background: theme.palette.background.paper,
                            height: 300
                        }}
                    >
                        <Typography variant="h6" fontWeight={700} sx={{ mb: 2 }}>
                            🎛️ Filters & Controls
                        </Typography>
                        <Stack spacing={2}>
                            <FormControl fullWidth size="small">
                                <InputLabel>School Year</InputLabel>
                                <Select
                                    value={schoolYear}
                                    label="School Year"
                                    onChange={(e) => setSchoolYear(e.target.value)}
                                >
                                    {schoolYearOptions.map((sy) => (
                                        <MenuItem key={sy} value={sy}>
                                            {sy}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl fullWidth size="small">
                                <InputLabel>Vendor</InputLabel>
                                <Select
                                    value={filterVendor}
                                    label="Vendor"
                                    onChange={(e) => setFilterVendor(e.target.value)}
                                >
                                    <MenuItem value="all">All Vendors</MenuItem>
                                    {vendorOptions.map((v) => (
                                        <MenuItem key={v} value={v}>
                                            {v}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl fullWidth size="small">
                                <InputLabel>School</InputLabel>
                                <Select
                                    value={filterSchool}
                                    label="School"
                                    onChange={(e) => setFilterSchool(e.target.value)}
                                >
                                    <MenuItem value="all">All Schools</MenuItem>
                                    {schoolOptions.map((s) => (
                                        <MenuItem key={s} value={s}>
                                            {s}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>

                            <FormControl fullWidth size="small">
                                <InputLabel>Review Status</InputLabel>
                                <Select
                                    value={filterReviewed}
                                    label="Review Status"
                                    onChange={(e) => setFilterReviewed(e.target.value)}
                                >
                                    <MenuItem value="all">All Items</MenuItem>
                                    <MenuItem value="notReviewed">Not Reviewed</MenuItem>
                                    <MenuItem value="reviewed">Reviewed</MenuItem>
                                </Select>
                            </FormControl>

                            <Alert severity="info" sx={{ mt: 2 }}>
                                <Typography variant="caption">
                                    📷 Items with images are highlighted with image icons. Click to view invoice images in detail.
                                </Typography>
                            </Alert>
                        </Stack>
                    </Paper>
                </Grid>

                {/* Chart */}
                <Grid item xs={12} lg={8}>
                    <Paper
                        elevation={0}
                        sx={{
                            p: 2,
                            borderRadius: theme.radii?.lg || 3,
                            border: `1px solid ${alpha(theme.palette.divider, 0.12)}`,
                            background: theme.palette.background.paper,
                        }}
                    >
                        <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
                            📈 Variance Trends Over Time
                        </Typography>

                        {lineChartData.length === 0 || vendorOptions.length === 0 ? (
                            <Box sx={{ textAlign: "center", py: 4 }}>
                                <ErrorOutlineIcon sx={{ fontSize: 48, color: theme.palette.text.secondary, mb: 2 }} />
                                <Typography variant="h6" color="text.secondary">
                                    No variance data available for this school year.
                                </Typography>
                            </Box>
                        ) : (
                            <ResponsiveContainer width="100%" height={240}>
                                <LineChart
                                    data={lineChartData}
                                    margin={{ left: 8, right: 8, top: 8, bottom: 8 }}
                                >
                                    <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
                                    <XAxis
                                        dataKey="date"
                                        tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
                                        minTickGap={20}
                                        stroke={theme.palette.text.secondary}
                                    />
                                    <YAxis
                                        allowDecimals={false}
                                        tick={{ fontSize: 10, fill: theme.palette.text.secondary }}
                                        stroke={theme.palette.text.secondary}
                                    />
                                    <Tooltip
                                        contentStyle={{
                                            backgroundColor: theme.palette.background.paper,
                                            border: `1px solid ${theme.palette.divider}`,
                                            borderRadius: theme.radii?.md || 8,
                                        }}
                                    />
                                    <Legend />
                                    {vendorOptions.slice(0, 3).map((vendor, idx) => {
                                        const isSelected =
                                            filterVendor !== "all" && vendor === filterVendor;
                                        return (
                                            <React.Fragment key={vendor}>
                                                <Line
                                                    type="monotone"
                                                    dataKey={`${vendor}_overpriced`}
                                                    name={`${vendor} (Over)`}
                                                    stroke={OVERPRICED_COLOR}
                                                    strokeDasharray="0"
                                                    dot={false}
                                                    strokeWidth={isSelected ? 3 : 1.5}
                                                    isAnimationActive={false}
                                                />
                                                <Line
                                                    type="monotone"
                                                    dataKey={`${vendor}_underpriced`}
                                                    name={`${vendor} (Under)`}
                                                    stroke={UNDERPRICED_COLOR}
                                                    strokeDasharray="5 5"
                                                    dot={false}
                                                    strokeWidth={isSelected ? 3 : 1.5}
                                                    isAnimationActive={false}
                                                />
                                            </React.Fragment>
                                        );
                                    })}
                                </LineChart>
                            </ResponsiveContainer>
                        )}
                    </Paper>
                </Grid>
            </Grid>

            {/* Tabbed Variance Analysis */}
            <Paper
                elevation={0}
                sx={{
                    p: 1,
                    mb: 2,
                    borderRadius: theme.radii?.lg || 3,
                    border: `1px solid ${alpha(theme.palette.divider, 0.12)}`
                }}
            >
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
                    <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)}>
                        <Tab
                            label={
                                <Box display="flex" alignItems="center" gap={1} sx={{ fontSize: '0.8rem' }}>
                                    <TrendingUpIcon fontSize="small" />
                                    <span>Overpriced ({metrics.overpricedCount})</span>
                                    <Badge badgeContent={metrics.overpricedItems.filter(i => i.hasImage).length} color="info">
                                        <ImageIcon fontSize="small" />
                                    </Badge>
                                </Box>
                            }
                        />
                        <Tab
                            label={
                                <Box display="flex" alignItems="center" gap={1} sx={{ fontSize: '0.8rem' }}>
                                    <TrendingDownIcon fontSize="small" />
                                    <span>Underpriced ({metrics.underpricedCount})</span>
                                    <Badge badgeContent={metrics.underpricedItems.filter(i => i.hasImage).length} color="info">
                                        <ImageIcon fontSize="small" />
                                    </Badge>
                                </Box>
                            }
                        />
                        <Tab
                            label={
                                <Box display="flex" alignItems="center" gap={1} sx={{ fontSize: '0.8rem' }}>
                                    <SchoolIcon fontSize="small" />
                                    <span>Off-Bid ({metrics.notOnBidCount})</span>
                                    <Badge badgeContent={metrics.notOnBidItems.filter(i => i.hasImage).length} color="info">
                                        <ImageIcon fontSize="small" />
                                    </Badge>
                                </Box>
                            }
                        />
                    </Tabs>
                </Box>

                {/* Tab Content */}
                <Box sx={{ height: 400 }}>
                    {activeTab === 0 && (
                        <StyledDataGridPro
                            rows={metrics.overpricedItems}
                            columns={itemColumns}
                            getRowId={(r) => r.InvoiceItemId}
                            density="compact"
                            disableRowSelectionOnClick
                            hideFooterPagination={metrics.overpricedItems.length <= 25}
                            initialState={{
                                pagination: { paginationModel: { pageSize: 25, page: 0 } },
                            }}
                            slots={{ toolbar: GridToolbar }}
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: { debounceMs: 300 },
                                    csvOptions: { fileName: `Overpriced_Items_${schoolYear}` },
                                },
                            }}
                            getRowClassName={(params) => params.row.hasImage ? 'has-image-row' : ''}
                        />
                    )}

                    {activeTab === 1 && (
                        <StyledDataGridPro
                            rows={metrics.underpricedItems}
                            columns={itemColumns}
                            getRowId={(r) => r.InvoiceItemId}
                            density="compact"
                            disableRowSelectionOnClick
                            hideFooterPagination={metrics.underpricedItems.length <= 25}
                            initialState={{
                                pagination: { paginationModel: { pageSize: 25, page: 0 } },
                            }}
                            slots={{ toolbar: GridToolbar }}
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: { debounceMs: 300 },
                                    csvOptions: { fileName: `Underpriced_Items_${schoolYear}` },
                                },
                            }}
                            getRowClassName={(params) => params.row.hasImage ? 'has-image-row' : ''}
                        />
                    )}

                    {activeTab === 2 && (
                        <StyledDataGridPro
                            rows={metrics.notOnBidItems}
                            columns={itemColumns.filter(col => col.field !== 'ContractPrice' && col.field !== 'VariancePerUnit')}
                            getRowId={(r) => r.InvoiceItemId}
                            density="compact"
                            disableRowSelectionOnClick
                            hideFooterPagination={metrics.notOnBidItems.length <= 25}
                            initialState={{
                                pagination: { paginationModel: { pageSize: 25, page: 0 } },
                            }}
                            slots={{ toolbar: GridToolbar }}
                            slotProps={{
                                toolbar: {
                                    showQuickFilter: true,
                                    quickFilterProps: { debounceMs: 300 },
                                    csvOptions: { fileName: `Off_Bid_Items_${schoolYear}` },
                                },
                            }}
                            getRowClassName={(params) => params.row.hasImage ? 'has-image-row' : ''}
                        />
                    )}
                </Box>
            </Paper>

            {/* Invoice Analysis Grid */}
            <Paper
                elevation={0}
                sx={{
                    p: 1,
                    mb: 2,
                    borderRadius: theme.radii?.lg || 3,
                    border: `1px solid ${alpha(theme.palette.divider, 0.12)}`
                }}
            >
                <Box display="flex" alignItems="center" gap={2} mb={1} px={1}>
                    <AssessmentIcon sx={{ color: theme.palette.primary.main }} />
                    <Typography variant="h6" fontWeight={700}>
                        📄 Invoice Analysis
                    </Typography>
                    <Chip
                        label={`${invoiceGroups.length} invoices`}
                        color="primary"
                        variant="outlined"
                        size="small"
                        sx={{ fontWeight: 600 }}
                    />
                    <Badge badgeContent={invoiceGroups.filter(inv => inv.hasImage).length} color="info">
                        <ImageIcon fontSize="small" />
                    </Badge>
                </Box>

                <StyledDataGridPro
                    rows={invoiceGroups}
                    columns={invoiceColumns}
                    getRowId={(r) => r.id}
                    density="compact"
                    disableRowSelectionOnClick
                    autoHeight
                    hideFooterPagination={invoiceGroups.length <= 25}
                    initialState={{
                        pagination: { paginationModel: { pageSize: 25, page: 0 } },
                    }}
                    getDetailPanelContent={({ row }) => (
                        <Box sx={{ p: 1 }}>
                            <Box display="flex" alignItems="center" gap={2} mb={1}>
                                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                                    📊 Items on invoice {row.InvoiceNumber}
                                </Typography>
                                {row.hasImage && (
                                    <Chip
                                        icon={<ImageIcon fontSize="small" />}
                                        label="Has Invoice Image"
                                        color="info"
                                        size="small"
                                        onClick={() => handleImageView(row.imageURL, row.InvoiceNumber)}
                                        clickable
                                    />
                                )}
                            </Box>
                            <StyledDataGridPro
                                autoHeight
                                density="compact"
                                rows={row.items}
                                columns={itemColumns.slice(0, -1)}
                                getRowId={(r) => r.InvoiceItemId}
                                hideFooter
                                disableRowSelectionOnClick
                                getRowClassName={(params) => params.row.hasImage ? 'has-image-row' : ''}
                            />
                        </Box>
                    )}
                    getDetailPanelHeight={() => "auto"}
                    getRowClassName={(params) => params.row.hasImage ? 'has-image-row' : ''}
                />
            </Paper>

            {/* Report Dialog */}
            <Dialog open={reportDialogOpen} onClose={() => setReportDialogOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Box display="flex" alignItems="center" gap={2}>
                        <AssessmentIcon sx={{ color: theme.palette.primary.main }} />
                        <Box>
                            <Typography variant="h6" fontWeight={700}>
                                📊 Enhanced Vendor Performance Report
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Executive dashboard with charts, action plans, detailed analysis, and invoice image links
                            </Typography>
                        </Box>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ pb: 1 }}>
                    <Alert severity="success" sx={{ mb: 3 }}>
                        <Typography variant="body2">
                            🎯 <strong>NEW: School Nutrition Department Focus!</strong> Enhanced reports now include executive dashboard,
                            public agency accountability requirements, vendor transparency protocols, full credit recovery guidance,
                            and comprehensive action plans specific to school district nutrition departments.
                        </Typography>
                    </Alert>

                    <Typography variant="h6" gutterBottom sx={{ mt: 2, fontWeight: 600 }}>
                        📋 Available Vendors ({schoolYear})
                    </Typography>

                    {vendorAnalytics.length === 0 ? (
                        <Box sx={{ textAlign: "center", py: 4 }}>
                            <BusinessIcon sx={{ fontSize: 48, color: theme.palette.text.secondary, mb: 2 }} />
                            <Typography variant="h6" color="text.secondary">
                                No vendor data available
                            </Typography>
                        </Box>
                    ) : (
                        <List sx={{ maxHeight: 300, overflow: 'auto' }}>
                            {vendorAnalytics.slice(0, 10).map((vendor, index) => (
                                <ListItem
                                    key={vendor.vendorName}
                                    button
                                    onClick={() => handleGenerateReport(vendor.vendorName)}
                                    sx={{ borderRadius: 1, mb: 0.5 }}
                                >
                                    <ListItemText
                                        primary={
                                            <Box display="flex" alignItems="center" gap={1}>
                                                <Typography variant="body1" fontWeight={600}>
                                                    {vendor.vendorName}
                                                </Typography>
                                                {vendor.invoices.some(inv => inv.imageURL) && (
                                                    <Badge badgeContent={vendor.invoices.filter(inv => inv.imageURL).length} color="info">
                                                        <ImageIcon fontSize="small" />
                                                    </Badge>
                                                )}
                                            </Box>
                                        }
                                        secondary={
                                            <Typography variant="body2" color="text.secondary">
                                                Net Impact: {vendor.netVariance >= 0 ? `+${fmtUSD(vendor.netVariance)}` : fmtUSD(vendor.netVariance)}
                                                {vendor.invoices.some(inv => inv.imageURL) && (
                                                    <span style={{ marginLeft: 8 }}>
                                                        📷 {vendor.invoices.filter(inv => inv.imageURL).length} invoices with images
                                                    </span>
                                                )}
                                            </Typography>
                                        }
                                    />
                                    <IconButton size="small" disabled={generatingReport}>
                                        <FileDownloadIcon />
                                    </IconButton>
                                </ListItem>
                            ))}
                        </List>
                    )}

                    {generatingReport && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                                📝 Generating executive dashboard report with invoice image links...
                            </Typography>
                            <LinearProgress />
                        </Box>
                    )}
                </DialogContent>
                <DialogActions sx={{ p: 3 }}>
                    <Button
                        onClick={() => setReportDialogOpen(false)}
                        disabled={generatingReport}
                        variant="outlined"
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Media Viewer */}
            {mediaViewerOpen && (
                <Dialog
                    open={mediaViewerOpen}
                    onClose={() => setMediaViewerOpen(false)}
                    maxWidth="lg"
                    fullWidth
                >
                    <DialogTitle>
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                            <Box display="flex" alignItems="center" gap={1}>
                                <ImageIcon sx={{ color: theme.palette.primary.main }} />
                                <Typography variant="h6">Invoice Image Viewer</Typography>
                                <IconButton
                                    size="small"
                                    onClick={() => handleImageUrlOpen(mediaUrl)}
                                    sx={{ ml: 1, color: theme.palette.primary.main }}
                                >
                                    <OpenInNewIcon fontSize="small" />
                                </IconButton>
                            </Box>
                            <IconButton onClick={() => setMediaViewerOpen(false)}>
                                <CloseIcon />
                            </IconButton>
                        </Box>
                    </DialogTitle>
                    <DialogContent sx={{ p: 1 }}>
                        {mediaType === "pdf" ? (
                            <iframe
                                src={mediaUrl}
                                width="100%"
                                height="600px"
                                title="Invoice PDF"
                                style={{ border: 'none', borderRadius: 8 }}
                            />
                        ) : (
                            <Box
                                component="img"
                                src={mediaUrl}
                                alt="Invoice Image"
                                sx={{
                                    width: '100%',
                                    height: 'auto',
                                    borderRadius: 2,
                                    boxShadow: theme.shadows[3]
                                }}
                                onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.parentNode.innerHTML = `
                                        <div style="text-align: center; padding: 2rem; color: ${theme.palette.text.secondary};">
                                            <p>Failed to load image</p>
                                            <p style="font-size: 0.8rem;">${mediaUrl}</p>
                                        </div>
                                    `;
                                }}
                            />
                        )}
                    </DialogContent>
                </Dialog>
            )}
        </Box>
    );
};

export default BidComparisonPage;
